import { AccordanceState } from '../../../../models/AccordanceState';
import Accordance from '../../../../models/Accordance';
import { AccordanceType } from '../../../../models/AccordanceType';
import { RolesIdEnum } from '../../../../models/RolesIdEnum';

export type FooterButtonsDecisionInput = {
  accordance: Accordance;
  isDraftValid?: boolean;
  hideApproveButtons?: boolean;
  isSubstantialModification?: boolean;
  confirmedModifiedSustantial?: boolean | null;
  proceedModification?: boolean | null;
  countTextArea?: number;
  rejectionJustification?: string;
  divChiefRole?: boolean;
  countryRepRole?: boolean;
  approvedByDivisionChief?: boolean;
  approvedByCountryRepresentative?: boolean;
  vps_managers?: boolean;
  vpc_managers?: boolean;
  approvedByVPSManager: boolean;
  approvedByVPCManager: boolean;
  canCreateInterGroupAccordances?: boolean;
  projectAssignedToMe?: boolean;
  canCreateOrEditAccordance?: boolean;
};

export type FooterButtonsDecision =
  | {
      type: 'pending-role-decision';
      approvalCompleted?: boolean;
      canSubmitDecision: boolean;
      decisionAction: 'approve' | 'reject';
      decisionText: 'approve_accordance' | 'accordance_rejecting' | 'Confirmar';
    }
  | { type: 'pending-vps-manager'; canApprove: boolean; approvalCompleted?: boolean }
  | { type: 'pending-vpc-manager'; canApprove: boolean; approvalCompleted?: boolean }
  | { type: 'pending-close-only' }
  | { type: 'pending-default' }
  | { type: 'reject'; canEdit: boolean; canDelete: boolean }
  | {
      type: 'draft';
      isDraftValid: boolean;
      canEdit: boolean;
      canDelete: boolean;
      canSubmitDraft: boolean;
      submitDraftText: 'approve_accordance' | 'send_for_approval';
    }
  | { type: 'valid'; actionsEnabled: boolean }
  | { type: 'default-close' };

export const getFooterButtonsDecision = ({
  accordance,
  isDraftValid = false,
  hideApproveButtons,
  isSubstantialModification,
  confirmedModifiedSustantial,
  proceedModification,
  countTextArea = 0,
  rejectionJustification = '',
  divChiefRole,
  countryRepRole,
  approvedByDivisionChief,
  approvedByCountryRepresentative,
  vps_managers,
  vpc_managers,
  approvedByVPSManager,
  approvedByVPCManager,
  canCreateInterGroupAccordances,
  projectAssignedToMe,
  canCreateOrEditAccordance,
}: FooterButtonsDecisionInput): FooterButtonsDecision => {
  const {
    accordanceState,
    currentUserCanApprove,
    criticalIssue,
    personInCharge,
    expirationDate,
    accordanceType,
    revisionDate,
    accordanceAgreementsType,
    rootCauseCategory,
    rootCause,
    linkedProductsAccordance,
    description,
    criticalIssueDescription,
    accordancesSubstantialFundamentalChanges,
    currentUserCanEdit,
    currentUserCanDelete,
  } = accordance;
  const roleToApproveId = accordance?.roleToApprove?.id;
  const canApprove = Boolean(currentUserCanApprove);
  const canEdit = Boolean(currentUserCanEdit) && Boolean(canCreateOrEditAccordance);
  const canDelete = currentUserCanDelete !== false;

  const canApproveSubstantialModification = confirmedModifiedSustantial === true && proceedModification === true;
  const canRejectSubstantialModification =
    (confirmedModifiedSustantial === false || proceedModification === false) &&
    countTextArea > 0 &&
    rejectionJustification.trim().length > 0;
  const isSubstantialAgreement =
    accordanceAgreementsType?.id === AccordanceType.Substantial && Boolean(isSubstantialModification);
  const isSpecialApproverRole =
    Boolean(divChiefRole) ||
    Boolean(countryRepRole) ||
    Boolean(vps_managers || Boolean(vpc_managers));
  const canShowCancelButton = !isSpecialApproverRole && isSubstantialAgreement;
  const canShowStandardPendingActions =
    accordanceState === AccordanceState.PENDING && !hideApproveButtons && canApprove;

  const getManagerApprovalDecision = (
    decisionType: 'pending-vps-manager' | 'pending-vpc-manager',
    currentRoleId: RolesIdEnum.GeneralManagerVPS | RolesIdEnum.General_Manager,
  ) => {
    const canStartManagerApprovalFlow = Boolean(approvedByCountryRepresentative);
    const noManagerHasApprovedYet = !approvedByVPSManager && !approvedByVPCManager;
    const currentRoleIsPendingApproval = roleToApproveId === currentRoleId;
    const otherManagerRoleAlreadyApproved =
      roleToApproveId === RolesIdEnum.GeneralManagerVPS || roleToApproveId === RolesIdEnum.General_Manager
        ? roleToApproveId !== currentRoleId
        : false;

    return {
      type: decisionType,
      canApprove: canStartManagerApprovalFlow && (noManagerHasApprovedYet || currentRoleIsPendingApproval),
      approvalCompleted: canStartManagerApprovalFlow && !noManagerHasApprovedYet && otherManagerRoleAlreadyApproved,
    } as FooterButtonsDecision;
  };

  if (canShowStandardPendingActions) {
    if (divChiefRole) {
      return {
        type: 'pending-role-decision',
        approvalCompleted: approvedByDivisionChief,
        canSubmitDecision: canApproveSubstantialModification || canRejectSubstantialModification,
        decisionAction: canApproveSubstantialModification ? 'approve' : 'reject',
        decisionText:
          canApproveSubstantialModification ||
          canRejectSubstantialModification ||
          proceedModification === false ||
          confirmedModifiedSustantial === false
            ? canApproveSubstantialModification
              ? 'approve_accordance'
              : 'accordance_rejecting'
            : 'Confirmar',
      };
    }

    if (countryRepRole) {
      return {
        type: 'pending-role-decision',
        approvalCompleted: approvedByCountryRepresentative,
        canSubmitDecision: canApproveSubstantialModification || canRejectSubstantialModification,
        decisionAction: canApproveSubstantialModification ? 'approve' : 'reject',
        decisionText:
          canApproveSubstantialModification ||
          canRejectSubstantialModification ||
          proceedModification === false ||
          confirmedModifiedSustantial === false
            ? canApproveSubstantialModification
              ? 'approve_accordance'
              : 'accordance_rejecting'
            : 'Confirmar',
      };
    }

    if (vps_managers) {
      return getManagerApprovalDecision('pending-vps-manager', RolesIdEnum.GeneralManagerVPS);
    }

    if (vpc_managers) {
      return getManagerApprovalDecision('pending-vpc-manager', RolesIdEnum.General_Manager);
    }

    if (canShowCancelButton) {
      return { type: 'pending-close-only' };
    }

    return { type: 'pending-default' };
  }

  if (accordanceState === AccordanceState.REJECT) {
    return { type: 'reject', canEdit, canDelete };
  }

  if (accordanceState === AccordanceState.DRAFT) {
    return {
      type: 'draft',
      isDraftValid,
      canEdit,
      canDelete,
      canSubmitDraft: Boolean(isDraftValid && canCreateInterGroupAccordances),
      submitDraftText: currentUserCanApprove ? 'approve_accordance' : 'send_for_approval',
    };
  }

  if (accordanceState === AccordanceState.VALID && !hideApproveButtons) {
    return {
      type: 'valid',
      actionsEnabled: Boolean(projectAssignedToMe),
    };
  }

  return { type: 'default-close' };
};
